import {
	Box,
	Button,
	Col,
	Grid,
	Inline,
	joinAttributes,
	Stack,
	Text,
} from "@sembark-travel/ui/base"
import {
	Form,
	MarkdownInputField,
	SubmissionError,
	TextInputField,
	validateFormValues,
	withServerErrors,
} from "@sembark-travel/ui/form"
import { showSnackbar } from "@sembark-travel/ui/snackbar"
import { useXHR } from "@sembark-travel/xhr"
import {
	IHotelBookingEnquiryEmailTemplate,
	IHotelBookingEnquiryEmailTemplateUpdate,
} from "./store"
import { validateVariables } from "./utils"
import * as Validator from "yup"

const EditHotelBookingEnquiryEmailTemplate = ({
	onCancel,
	onSubmit,
	initialValues,
}: {
	onCancel: () => void
	onSubmit: () => void
	initialValues: IHotelBookingEnquiryEmailTemplateUpdate
}) => {
	const xhr = useXHR()
	return (
		<Form<IHotelBookingEnquiryEmailTemplate>
			initialValues={initialValues}
			validate={validateFormValues(
				Validator.object().shape({
					subject_line: Validator.string().required("Subject is required"),
					greetings: Validator.string().required("Greeting is required"),
				})
			)}
			subscription={{ submitting: true }}
			onSubmit={withServerErrors(async (data) => {
				const { greetings, subject_line } = data
				const subjectAllowedVariables =
					initialValues.variables.for_subject_line.map((v) => v.placeholder)
				const greetingsAllowedVariables =
					initialValues.variables.for_greetings.map((v) => v.placeholder)
				const validationErrors = [
					validateVariables(subject_line, subjectAllowedVariables, "subject"),
					validateVariables(greetings, greetingsAllowedVariables, "greetings"),
				]
					.filter(Boolean)
					.map((err) => err?.message)

				if (validationErrors.length) {
					throw new Error(validationErrors.join("\n"))
				}

				await xhr.post("/trip-hotel-booking-email-templates", data)
				onSubmit()
			})}
		>
			{({ submitting, handleSubmit, form }) => (
				<form noValidate onSubmit={handleSubmit}>
					<Stack gap="4">
						<Grid gap="6" flexDirection="col">
							<Col>
								<Stack gap="4">
									<Box>
										<TextInputField
											name="subject_line"
											label="Subject"
											type="text"
											placeholder="enter email subject"
											required
											min={10}
											max={100}
											secondaryLabel="only for Email sharing"
										/>
										<Stack>
											<Text>
												You can use following placeholder variables in the
												subject
											</Text>
											<Box>
												{joinAttributes(
													...initialValues.variables.for_subject_line.map(
														(variable) => (
															<Button
																key={variable.id}
																inline
																title={variable.description}
																onClick={(
																	e: React.MouseEvent<HTMLButtonElement>
																) => {
																	e.preventDefault()
																	const newSubject =
																		(form.getState().values.subject_line ||
																			"") + ` ${variable.placeholder}`

																	form.change("subject_line", newSubject.trim())
																	showSnackbar("Added variable to subject.")
																}}
															>
																{variable.placeholder}
															</Button>
														)
													)
												)}
											</Box>
										</Stack>
									</Box>
									<Box>
										<MarkdownInputField
											name="greetings"
											label="Greetings"
											required
											max={1000}
											placeholder="enter greetings content"
											rows={10}
											secondaryLabel="for WA & Email sharing"
										/>
										<Stack>
											<Text>
												You can use following placeholder variables in the
												greatings
											</Text>
											<Box>
												{joinAttributes(
													...initialValues.variables.for_greetings.map(
														(variable) => (
															<Button
																key={variable.id}
																inline
																title={variable.description}
																onClick={(
																	e: React.MouseEvent<HTMLButtonElement>
																) => {
																	e.preventDefault()
																	const newGreetings =
																		(form.getState().values.greetings || "") +
																		` ${variable.placeholder}`

																	form.change("greetings", newGreetings.trim())
																	showSnackbar("Added variable to greetings.")
																}}
															>
																{variable.placeholder}
															</Button>
														)
													)
												)}
											</Box>
										</Stack>
									</Box>
								</Stack>
							</Col>
						</Grid>
						<SubmissionError />
						<Grid gap="0">
							<Col>
								<Stack gap="4" alignItems="end">
									<Inline gap="4">
										<Button type="submit" disabled={submitting}>
											{submitting ? "Please wait..." : "Save Template"}
										</Button>
										<Button
											disabled={submitting}
											onClick={() => onCancel()}
											level="tertiary"
										>
											Cancel
										</Button>
									</Inline>
								</Stack>
							</Col>
						</Grid>
					</Stack>
				</form>
			)}
		</Form>
	)
}

export default EditHotelBookingEnquiryEmailTemplate
